import { apiRequest } from "./api";

export interface PointConfig {
  id: string;
  club_id: string | null;
  action_type: string;
  points: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  club?: {
    id: string;
    name: string;
  };
}

export interface ActionType {
  code: string;
  label: string;
}

export interface EffectivePointsMap {
  [action_type: string]: number;
}

/**
 * Get point configurations
 */
export async function getPointConfigs(params?: {
  club_id?: string | null;
  action_type?: string;
}): Promise<PointConfig[]> {
  const queryParams = new URLSearchParams();

  if (params?.club_id !== undefined) {
    queryParams.append("club_id", params.club_id === null ? "null" : params.club_id);
  }

  if (params?.action_type) {
    queryParams.append("action_type", params.action_type);
  }

  const url = queryParams.toString()
    ? `/points/config?${queryParams.toString()}`
    : "/points/config";

  return apiRequest<PointConfig[]>(url, { method: "GET" });
}

/**
 * Get effective point values for a club (club-specific or platform default)
 */
export async function getEffectivePoints(clubId: string): Promise<EffectivePointsMap> {
  return apiRequest<EffectivePointsMap>(`/points/config/effective/${clubId}`, {
    method: "GET",
  });
}

/**
 * Create or update a point configuration
 */
export async function savePointConfig(data: {
  club_id: string | null;
  action_type: string;
  points: number;
}): Promise<PointConfig> {
  return apiRequest<PointConfig>("/points/config", {
    method: "POST",
    data: data,
  });
}

/**
 * Update a point configuration (toggle active status or change points)
 */
export async function updatePointConfig(
  id: string,
  data: {
    points?: number;
    is_active?: boolean;
  }
): Promise<PointConfig> {
  return apiRequest<PointConfig>(`/points/config/${id}`, {
    method: "PATCH",
    data: data,
  });
}

/**
 * Delete a point configuration
 */
export async function deletePointConfig(id: string): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(`/points/config/${id}`, {
    method: "DELETE",
  });
}

/**
 * Get list of available action types
 */
export async function getActionTypes(): Promise<ActionType[]> {
  return apiRequest<ActionType[]>("/points/action-types", {
    method: "GET",
  });
}
